<template>
  <v-card flat class="rmr-table-shell pbv-preview">
    <rmr-table-header>
      <template #heading>
        <div class="rmr-table-shell__eyebrow">
          Étape 1 · {{ badgeLabel }}
          <span v-if="durationLabel"> · {{ durationLabel }}</span>
          <span v-if="providerLabel" class="pbv-preview__provider">
            · {{ providerLabel }}
          </span>
        </div>
        <div class="rmr-table-shell__title">
          {{ source.title || source.url }}
        </div>
      </template>
      <template #controls>
        <rmr-soft-badge
          v-if="source.is_video"
          :label="source.has_timestamps ? 'time codes' : 'sans time codes'"
          :color="
            source.has_timestamps ? tokens.RMR_SUCCESS : tokens.RMR_WARNING
          "
          size="sm"
        />
      </template>
    </rmr-table-header>

    <v-card-text class="rmr-table-shell__body">
      <!-- La brique par défaut a échoué et c'est le fallback qui a transcrit :
           l'éditeur doit le voir, sinon une brique en panne passe inaperçue. -->
      <rmr-notice
        v-if="source.warning"
        variant="warning"
        class="mb-4"
        title="Transcription par la brique de fallback"
        :text="source.warning"
      />

      <!-- La source n'est PAS une vidéo : on ne bloque pas, le back sait très
           bien scraper un article. Mais l'éditeur doit le savoir avant de
           lancer une rédaction sur cette page. -->
      <rmr-notice
        v-if="!source.is_video"
        variant="info"
        class="mb-4"
        text="Cette URL n'est pas une vidéo : son contenu a été scrapé comme un article. La rédaction fonctionnera, sans transcription."
      />

      <!-- Description publiée avec la vidéo : contexte pour l'éditeur, et
           pour le LLM (elle part avec la transcription à la génération). -->
      <div v-if="source.video_description" class="pbv-preview__desc">
        <button
          type="button"
          class="pbv-preview__desc-toggle"
          @click="descriptionOpen = !descriptionOpen"
        >
          <v-icon size="16">{{
            descriptionOpen ? icons.mdiChevronUp : icons.mdiChevronDown
          }}</v-icon>
          Description de la vidéo
        </button>
        <p v-if="descriptionOpen" class="pbv-preview__desc-text">
          {{ source.video_description }}
        </p>
      </div>

      <label class="rmr-table-shell__filter-label pbv-preview__label">
        {{ source.is_video ? "Transcription" : "Contenu extrait" }}
        <span class="pbv-preview__count">{{ charCount }}</span>
        <span v-if="video.transcriptDirty.value" class="pbv-preview__dirty">
          · modifiée, non enregistrée
        </span>
      </label>
      <!--
        Éditable : la rédaction corrige un nom propre ou retire une phrase
        parasite ici, et c'est ce texte que la génération relira. Les time codes
        survivent à la correction (réalignement côté serveur).
      -->
      <rmr-textarea
        :value="video.state.transcriptDraft"
        :readonly="!source.is_video"
        rows="10"
        no-resize
        hide-details
        class="pbv-preview__text"
        @input="video.state.transcriptDraft = $event || ''"
      />

      <div v-if="source.is_video" class="pbv-preview__edit-actions">
        <rmr-action-button
          v-if="video.transcriptDirty.value"
          label="Annuler les modifications"
          variant="ghost"
          size="sm"
          @click="video.resetTranscriptDraft"
        />
        <rmr-action-button
          label="Enregistrer la transcription"
          :icon="icons.mdiContentSave"
          variant="utility"
          size="sm"
          :loading="video.state.savingTranscript"
          :disabled="!video.canSaveTranscript.value"
          @click="video.saveTranscript"
        />
      </div>

      <rmr-notice
        v-if="video.transcriptDirty.value"
        variant="warning"
        class="mt-3"
        text="Enregistrez (ou annulez) la modification avant de proposer des angles : ils sont calculés sur la transcription enregistrée."
      />

      <div class="pbv-preview__next">
        <rmr-toggle-card
          :value="video.state.split"
          label="Découper la vidéo en plusieurs angles"
          size="sm"
          :disabled="!video.canSplit.value"
          @input="video.state.split = $event"
        />
        <span v-if="!video.canSplit.value" class="pbv-preview__hint">
          Sans time codes, la vidéo donne un seul angle.
        </span>
        <v-spacer />
        <rmr-action-button
          label="Proposer des angles"
          :icon="icons.mdiCreation"
          variant="primary"
          size="md"
          :loading="video.state.proposing"
          :disabled="!video.canPropose.value"
          @click="video.proposeAngles()"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiContentSave,
  mdiCreation,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrTextarea from "@/components/ui/forms/RmrTextarea.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import * as tokens from "@/styles/tokens";

const PLATFORM_LABELS = {
  youtube: "Vidéo YouTube",
  instagram: "Vidéo Instagram",
  tiktok: "Vidéo TikTok",
  x: "Vidéo X",
  other: "Vidéo (site non reconnu)",
};

export default {
  name: "SourcePreviewCard",
  components: {
    RmrActionButton,
    RmrNotice,
    RmrSoftBadge,
    RmrTableHeader,
    RmrTextarea,
    RmrToggleCard,
  },
  props: {
    /** Le hook `useVideoTranscription` de la page (état + actions). */
    video: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const descriptionOpen = ref(false);

    // =>  COMPUTED
    const source = computed(() => props.video.state.source || {});

    const badgeLabel = computed(() => {
      if (!source.value.is_video) return "Article";
      return PLATFORM_LABELS[source.value.video_platform] || "Vidéo";
    });

    // La durée manque sur certaines plateformes (mesuré : yt-dlp ne remonte
    // aucune durée sur les réels Instagram). On n'affiche donc rien plutôt
    // qu'un « 0 s » trompeur.
    const durationLabel = computed(() => {
      const seconds = source.value.video_duration_s;
      if (!seconds) return "";
      const minutes = Math.floor(seconds / 60);
      const rest = seconds % 60;
      return minutes ? `${minutes} min ${rest} s` : `${rest} s`;
    });

    /**
     * La brique qui a RÉELLEMENT produit le transcript — pas celle du
     * sélecteur : en « Par défaut », l'éditeur ne sait pas laquelle a tourné.
     * `provider` vaut `key[:modèle][:edited]` ; on montre le libellé du
     * catalogue quand on l'a, la clé brute sinon.
     */
    const providerLabel = computed(() => {
      const raw = source.value.provider || "";
      if (!raw) return "";
      const [key] = raw.split(":");
      const known = props.video.state.providers.find(
        (item) => item.key === key
      );
      const label = known ? known.label : key;
      return raw.endsWith(":edited") ? `${label} (corrigée à la main)` : label;
    });

    const charCount = computed(
      () => `${(props.video.state.transcriptDraft || "").length} caractères`
    );

    // =>  DATA
    return {
      badgeLabel,
      charCount,
      descriptionOpen,
      durationLabel,
      icons: { mdiChevronDown, mdiChevronUp, mdiContentSave, mdiCreation },
      providerLabel,
      source,
      tokens,
    };
  },
};
</script>

<style lang="scss" scoped>
.pbv-preview__provider {
  color: $rmr-indigo;
}

.pbv-preview__desc {
  margin-bottom: 14px;
}

.pbv-preview__desc-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  color: $rmr-text-secondary;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: $rmr-text-primary;
  }
}

.pbv-preview__desc-text {
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-1;
  color: $rmr-text-secondary;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.pbv-preview__label {
  display: block;
  margin-bottom: 6px;
}

.pbv-preview__count {
  margin-left: 6px;
  font-weight: 400;
  text-transform: none;
}

.pbv-preview__dirty {
  margin-left: 6px;
  font-weight: 600;
  text-transform: none;
  color: $rmr-orange;
}

// La transcription peut être longue : on la borne pour que les actions
// restent visibles sans avoir à faire défiler la page.
.pbv-preview__text ::v-deep textarea {
  max-height: 320px;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.55;
}

.pbv-preview__edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.pbv-preview__next {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid $rmr-border;
}

.pbv-preview__hint {
  font-size: 12.5px;
  color: $rmr-text-subtle;
}
</style>
